import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, ItemListSchema } from '@/components/seo/seo';
import { useState, useMemo } from 'react';
import { COUNCIL_REPORT_MANIFEST } from '@fixtyer/content';

interface CouncilEntry {
  slug: string;
  name: string;
  rating: 'red' | 'amber' | 'green';
  headline: string;
  region: string;
}

interface CouncilEditorialHeadline {
  slug: string;
  headline: string;
}

/** Editorial-only copy; identity, rating and region come from the manifest. */
const councilEditorialHeadlines: readonly CouncilEditorialHeadline[] = [
  { slug: 'barking-and-dagenham', headline: '48.6% of A-Roads failing' },
  { slug: 'barnet', headline: '89% prevention spend, 39% more potholes' },
  { slug: 'barnsley', headline: '48% surge despite £17m investment' },
  { slug: 'bath-and-north-east-somerset', headline: '71% pothole surge — council admits "holding the decline"' },
  { slug: 'bedford', headline: 'U-roads collapsed to 20/100 — two-tier system exposed' },
  { slug: 'bexley', headline: '424% U-Road failures increase' },
  { slug: 'birmingham', headline: '91% prevention spend collapse' },
  { slug: 'blackburn-with-darwen', headline: 'Only 1% resurfaced a year — a 122-year cycle' },
  { slug: 'blackpool', headline: 'Good-condition A-roads down 28% since 2020/21' },
  { slug: 'bolton', headline: '80% of roads invisible to inspectors' },
  { slug: 'bournemouth-christchurch-and-poole', headline: 'Repairs up 34% despite prevention-first claims' },
  { slug: 'bracknell-forest', headline: '0% prevention spend in 2020-21' },
  { slug: 'bradford', headline: '83% increase despite more spending' },
  { slug: 'brent', headline: 'Zero prevention funding 2025/26' },
  { slug: 'brighton-and-hove', headline: 'Survey scandal & £96m backlog' },
  { slug: 'bristol', headline: 'Every road worse, +65% potholes' },
  { slug: 'bromley', headline: '£10m gamble & 67% deterioration spike' },
  { slug: 'buckinghamshire', headline: '149,315 defects repaired in 5 years — B/C roads still declining' },
  { slug: 'bury', headline: 'One in five residential roads still in RED condition' },
  { slug: 'calderdale', headline: 'B/C roads in RED doubled to 10.6% in 2024' },
  { slug: 'cambridgeshire', headline: '277,406 potholes in five years on sinking peatland roads' },
  { slug: 'camden', headline: '7,300 defects fixed — only 340 counted as potholes' },
  { slug: 'central-bedfordshire', headline: 'Good B/C roads fell to 76% — pothole counts are estimates' },
  { slug: 'cheshire-east', headline: 'Record 37,766 potholes filled in 2024/25 alone' },
  { slug: 'cheshire-west-and-chester', headline: 'U-road RED jumped to 17.77% in the 2024 survey' },
  { slug: 'city-of-london', headline: 'A quarter of the Square Mile needs resurfacing' },
  { slug: 'cornwall', headline: '36,501 potholes filled in a year on a network rated GREEN' },
  { slug: 'coventry', headline: 'GREEN rated despite £13.6m annual funding gap' },
  { slug: 'croydon', headline: '43.6% of residential roads in RED when last surveyed' },
  { slug: 'cumberland', headline: '52% of U-roads in RED or amber condition' },
  { slug: 'darlington', headline: 'GREEN rated — 38.9% of B/C roads amber on a 558km network' },
  { slug: 'derby', headline: 'A-road green share fell from 79% to 30% since 2022' },
  { slug: 'derbyshire', headline: 'RED overall and condition; AMBER on spend and best practice' },
  { slug: 'devon', headline: '52% of 13,000km network in managed decline' },
  { slug: 'doncaster', headline: '13% of 1,150km of residential roads in RED condition' },
  { slug: 'dorset', headline: '43,115 potholes in five years — B/C RED spiked to 13.31%' },
  { slug: 'dudley', headline: 'U-roads at 42/100 — 83% of network neglected' },
  { slug: 'durham', headline: '198,463 pothole repairs in five years — 95% of claims refused' },
  { slug: 'ealing', headline: 'Needs £8.5m a year just to stand still' },
  { slug: 'enfield', headline: 'Preventative spend collapsed from 72% to 25%' },
  { slug: 'east-riding-of-yorkshire', headline: 'RED on spend — 22% of U-roads in RED condition' },
  { slug: 'east-sussex', headline: 'AMBER on every scorecard — 18% of U-roads in RED' },
  { slug: 'essex', headline: 'GREEN-rated Essex doubled pothole repairs to 18,636 a year' },
  { slug: 'gateshead', headline: '18% of 682km of estate roads need maintenance' },
  { slug: 'gloucestershire', headline: 'GREEN rated — yet 47,981 potholes filled in 2024/25' },
  { slug: 'greenwich', headline: '59% of C-Roads in RED condition' },
  { slug: 'hackney', headline: '19% of residential roads RED at the last survey' },
  { slug: 'halton', headline: '27% of U-roads RED — up from 5% in 2021' },
  { slug: 'hammersmith-and-fulham', headline: '676 potholes filled in one year on a 203km network' },
  { slug: 'hampshire', headline: '91,661 defects in 2022/23' },
  { slug: 'haringey', headline: 'Nearly one in five B/C roads now in RED condition' },
  { slug: 'harrow', headline: '7,055 pothole repairs in one year on 457km of roads' },
  { slug: 'hartlepool', headline: '33% of estate roads failed in 2022 — re-survey declined' },
  { slug: 'havering', headline: '27% of residential roads RED when surveys stopped in 2022' },
  { slug: 'herefordshire', headline: 'B/C roads in RED doubled to 13% by 2024' },
  { slug: 'hertfordshire', headline: '38,806 potholes a year — and a £29m U-road target in reverse' },
  { slug: 'hillingdon', headline: 'GREEN rated — yet 1,023 potholes patched in 2024/25' },
  { slug: 'hounslow', headline: '25-year PFI — GREEN condition but AMBER overall' },
  { slug: 'hull', headline: 'Strategic bus-route RED more than doubled in 2024' },
  { slug: 'isle-of-wight', headline: '25-year PFI — 41,901 pothole fills in five years' },
  { slug: 'isles-of-scilly', headline: '14.48km network — England\'s smallest highway authority' },
  { slug: 'islington', headline: 'For every RED road fixed, another slips to amber' },
  { slug: 'kensington-and-chelsea', headline: 'Zero capital investment for three years' },
  { slug: 'kent', headline: '39% of unclassified roads RED in 2024 — up from 18% in a year' },
  { slug: 'kingston-upon-thames', headline: 'Good-condition roads fell from 97% to 72% in four years' },
  { slug: 'kirklees', headline: '29% of U-roads in RED — nearly double since 2020' },
  { slug: 'lambeth', headline: 'One-third of A-roads in RED — just 16% green' },
  { slug: 'knowsley', headline: '69% of U-roads now in amber or RED condition' },
  { slug: 'lancashire', headline: '97,757 potholes filled in 2024/25 — up 94% in five years' },
  { slug: 'leeds', headline: 'GREEN rated despite AMBER roads — 26,007 potholes in 2024/25' },
  { slug: 'leicester', headline: 'U-road RED rose from 17% to 24% on an 830km network' },
  { slug: 'leicestershire', headline: 'Potholes up 47% in five years — RED rated' },
  { slug: 'lewisham', headline: 'GREEN rated — under half of B/C roads still green' },
  { slug: 'lincolnshire', headline: '327,165 potholes in five years — AMBER on every DfT scorecard' },
  { slug: 'liverpool', headline: 'Defect repairs quintupled to 9,547 as resurfacing collapsed 86%' },
  { slug: 'luton', headline: '411km of residential U-roads — 86% of the network' },
  { slug: 'manchester', headline: 'GREEN rated despite AMBER condition — 15% of U-roads RED' },
  { slug: 'medway', headline: '31% of U-roads RED — best practice also rated RED' },
  { slug: 'merton', headline: 'RED condition — 16.6% of residential roads "very poor"' },
  { slug: 'middlesbrough', headline: 'GREEN rated — yet 26% of estate roads in RED condition' },
  { slug: 'milton-keynes', headline: '26% of estate roads RED — quintupled since 2020' },
  { slug: 'newham', headline: '367km of residential roads on a two-year survey cycle' },
  { slug: 'newcastle', headline: '0% preventative spend five years running — RED best practice' },
  { slug: 'norfolk', headline: 'Half of the 9,944km network surveyed once every four years' },
  { slug: 'north-east-lincolnshire', headline: '31.8% of U-roads RED — nearly doubled since 2020' },
  { slug: 'north-lincolnshire', headline: '£5.8M unspent while roads crumble' },
  { slug: 'north-northamptonshire', headline: 'RED on best practice — only 8.58% of B/C roads in good condition' },
  { slug: 'north-somerset', headline: '8,600 potholes planned — council admits survey data gap' },
  { slug: 'north-tyneside', headline: 'A-road RED up from 2% to 14% after survey switch' },
  { slug: 'north-yorkshire', headline: 'AMBER on every measure — RED U-roads up 64% in two years' },
  { slug: 'northumberland', headline: '212,069 potholes in five years on a 5,209km network' },
  { slug: 'nottingham', headline: 'B/C roads in RED tripled from 7% to 21% since 2020' },
  { slug: 'nottinghamshire', headline: '416,380 potholes filled in five years — about 228 every day' },
  { slug: 'oldham', headline: 'GREEN condition but RED best practice — 46% reactive spend' },
  { slug: 'oxfordshire', headline: '157,406 potholes in five years — B/C RED at a five-year high' },
  { slug: 'peterborough', headline: '29% of U-roads RED at peak — 25% surveyed yearly' },
  { slug: 'plymouth', headline: '37% of residential roads in RED condition' },
  { slug: 'portsmouth', headline: 'All-GREEN with a 25-year PFI — 6,890 pothole repairs' },
  { slug: 'reading', headline: '79% of U-roads green — DfT flags incomplete data' },
  { slug: 'redbridge', headline: 'Half of residential roads in RED condition' },
  { slug: 'redcar-and-cleveland', headline: '12,635 potholes in 2023/24 as resurfacing halved' },
  { slug: 'richmond-upon-thames', headline: 'RED condition — half of U-roads needed resurfacing in 2021' },
  { slug: 'rochdale', headline: 'RED best practice — 71.77% of reactive spend on potholes' },
  { slug: 'rotherham', headline: 'All-GREEN rated — yet 15,936 pothole repairs in 2024/25' },
  { slug: 'rutland', headline: 'One in five village roads RED for five years running' },
  { slug: 'salford', headline: '35% of residential roads in RED — more than double since 2020' },
  { slug: 'sandwell', headline: 'GREEN rated — 23% of U-roads in RED, up from 15%' },
  { slug: 'sefton', headline: '22% of estate roads in RED at the last comparable survey' },
  { slug: 'sheffield', headline: 'U-road RED up from 8% to 12% — PFI hides the spend data' },
  { slug: 'shropshire', headline: '46% of the network not condition-surveyed since 2020' },
  { slug: 'slough', headline: 'Bankrupt under Section 114 — potholes up 269%' },
  { slug: 'solihull', headline: 'Pothole repairs doubled to 1,640 in a single year' },
  { slug: 'somerset', headline: 'RED money, GREEN roads — spending £1m under its DfT allocation' },
  { slug: 'south-gloucestershire', headline: '£12m shortfall — pothole repairs doubled since 2020' },
  { slug: 'south-tyneside', headline: '9,594 pothole repairs in one year — up 66%' },
  { slug: 'southampton', headline: 'Council says 70% of roads need capital funding' },
  { slug: 'southend-on-sea', headline: 'Only 18% of residential roads in good condition' },
  { slug: 'southwark', headline: 'Only 40% of A-roads remain in green condition' },
  { slug: 'st-helens', headline: '75% of B/C roads in amber or RED condition in 2024' },
  { slug: 'staffordshire', headline: '37,308 potholes in one year — RED C roads doubled since 2020' },
  { slug: 'stockport', headline: '11% of 798km of U-roads in RED condition' },
  { slug: 'stockton-on-tees', headline: '22% of estate roads RED — doubled since 2021' },
  { slug: 'stoke-on-trent', headline: '87% of the network has no SCANNER survey data' },
  { slug: 'suffolk', headline: '118 miles of A-roads sliding towards RED' },
  { slug: 'sunderland', headline: '23% of residential roads now in RED — up from 18%' },
  { slug: 'surrey', headline: '273,383 potholes filled in five years — and the trend is rising' },
  { slug: 'sutton', headline: 'RED on condition AND best practice — 326km of U-roads' },
  { slug: 'swindon', headline: '14,411 pothole fills in a single year' },
  { slug: 'tameside', headline: '15.4% of U-roads RED on a four-year survey cycle' },
  { slug: 'telford-and-wrekin', headline: '31% of B/C roads flagged amber for maintenance' },
  { slug: 'thurrock', headline: 'U-road RED rate nearly tripled since 2020' },
  { slug: 'torbay', headline: '58% more potholes filled since 2021/22' },
  { slug: 'tower-hamlets', headline: '20% of the network sits beside active construction' },
  { slug: 'trafford', headline: 'Just 24.5% preventative spend — RED on best practice' },
  { slug: 'wakefield', headline: '18% of residential roads now in RED condition' },
  { slug: 'walsall', headline: 'One in four residential roads in RED condition' },
  { slug: 'waltham-forest', headline: 'Potholes up 109% while resurfacing cut 40%' },
  { slug: 'wandsworth', headline: '372km of U-roads judged on 2021 survey data' },
  { slug: 'warrington', headline: '22% U-road RED peak — 19k pothole fills in 5 years' },
  { slug: 'warwickshire', headline: 'AMBER on all four scorecards — B/C RED roads up 45%' },
  { slug: 'west-berkshire', headline: 'All-GREEN — yet 9,983 pothole fills in five years' },
  { slug: 'west-northamptonshire', headline: 'Only 7.57% B/C roads in good condition' },
  { slug: 'west-sussex', headline: '17% of U-roads in RED after a three-year rise' },
  { slug: 'westminster', headline: 'One million daytime users on 339km of roads' },
  { slug: 'westmorland-and-furness', headline: '34,228 potholes in one year — 94 a day' },
  { slug: 'wigan', headline: 'GREEN rated — yet 16.4% of U-roads in RED condition' },
  { slug: 'wiltshire', headline: 'GREEN rated — yet 14,758 pothole repairs in 2024/25' },
  { slug: 'windsor-and-maidenhead', headline: '348km of U-roads with no published condition data' },
  { slug: 'wirral', headline: 'Only 25% of residential roads in GREEN condition' },
  { slug: 'wokingham', headline: 'U-road RED readings swung between 3% and 21%' },
  { slug: 'wolverhampton', headline: 'Planning 7,500 repairs, +46% spike' },
  { slug: 'worcestershire', headline: 'GREEN rated but prevention declining -20%' },
  { slug: 'york', headline: '25% of U-roads RED — up from 11% in 2019/20' },
];

const editorialBySlug = new Map(
  councilEditorialHeadlines.map((entry) => [entry.slug, entry.headline])
);
const councils: CouncilEntry[] = COUNCIL_REPORT_MANIFEST
  .filter((entry) => entry.reportUrl)
  .map((entry) => ({
    slug: entry.slug,
    name: entry.displayName,
    rating: entry.ratings.overall,
    headline: editorialBySlug.get(entry.slug) ?? entry.claimSafeSummary,
    region: entry.region,
  }));

const regions = Array.from(new Set(councils.map(c => c.region))).sort();

const ratingLabels = {
  red: 'RED',
  amber: 'AMBER',
  green: 'GREEN',
};

const ratingTheme = {
  red: {
    bar: 'bg-red-500',
    pill: 'bg-red-100 text-red-700',
    avatar: 'bg-red-50 text-red-700 ring-1 ring-red-200/70',
    hover: 'hover:border-red-300',
  },
  amber: {
    bar: 'bg-amber-500',
    pill: 'bg-amber-100 text-amber-700',
    avatar: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/70',
    hover: 'hover:border-amber-300',
  },
  green: {
    bar: 'bg-emerald-500',
    pill: 'bg-emerald-100 text-emerald-700',
    avatar: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70',
    hover: 'hover:border-emerald-300',
  },
} as const;

const connectorWords = new Set(['and', '&', 'upon', 'of', 'on', 'with', 'the']);

function getInitials(name: string): string {
  const words = name
    .replace(/&/g, ' ')
    .split(/\s+/)
    .filter((word) => word && !connectorWords.has(word.toLowerCase()));
  if (words.length === 0) return name.slice(0, 2).toUpperCase();
  const first = words[0] ?? '';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const second = words[1] ?? '';
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

function RatingBar({
  red,
  amber,
  green,
  className = '',
}: {
  red: number;
  amber: number;
  green: number;
  className?: string;
}) {
  const total = red + amber + green || 1;
  const pct = (n: number) => `${(n / total) * 100}%`;
  return (
    <div className={`flex w-full overflow-hidden rounded-full bg-surface-100 ${className}`}>
      {red > 0 && <div className="bg-red-500" style={{ width: pct(red) }} />}
      {amber > 0 && <div className="bg-amber-500" style={{ width: pct(amber) }} />}
      {green > 0 && <div className="bg-emerald-500" style={{ width: pct(green) }} />}
    </div>
  );
}

function CouncilCard({ council }: { council: CouncilEntry }) {
  const theme = ratingTheme[council.rating];
  return (
    <Link
      to={`/councils/${council.slug}`}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border-2 border-surface-200 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${theme.hover}`}
    >
      <span className={`absolute inset-x-0 top-0 h-1.5 ${theme.bar}`} aria-hidden="true" />
      <div className="flex flex-1 flex-col p-6 pt-7">
        <div className="mb-4 flex items-start gap-3">
          <span
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl font-display text-sm font-bold ${theme.avatar}`}
            aria-hidden="true"
          >
            {getInitials(council.name)}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-semibold leading-snug text-brand-950 transition-colors group-hover:text-brand-700">
              {council.name}
            </h3>
            <span className="text-xs text-surface-500">{council.region}</span>
          </div>
          <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${theme.pill}`}>
            {ratingLabels[council.rating]}
          </span>
        </div>
        <p className="mb-5 text-sm text-surface-600 line-clamp-2">{council.headline}</p>
        <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-brand-600 transition-colors group-hover:text-accent-600">
          View report
          <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

type RatingFilter = 'all' | 'red' | 'amber' | 'green';

export function CouncilsHubPage() {
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    return councils.filter((c) => {
      const matchesSearch = search === '' || 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.headline.toLowerCase().includes(search.toLowerCase());
      const matchesRating = ratingFilter === 'all' || c.rating === ratingFilter;
      const matchesRegion = regionFilter === 'all' || c.region === regionFilter;
      return matchesSearch && matchesRating && matchesRegion;
    });
  }, [search, ratingFilter, regionFilter]);

  // Group results by region for a more navigable layout. When the user has
  // already narrowed to a single region we fall back to a flat grid.
  const groupedByRegion = useMemo(() => {
    if (regionFilter !== 'all') return null;
    const map = new Map<string, CouncilEntry[]>();
    for (const council of filtered) {
      const existing = map.get(council.region);
      if (existing) existing.push(council);
      else map.set(council.region, [council]);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([region, list]) => ({
        region,
        list,
        red: list.filter((c) => c.rating === 'red').length,
        amber: list.filter((c) => c.rating === 'amber').length,
        green: list.filter((c) => c.rating === 'green').length,
      }));
  }, [filtered, regionFilter]);

  const redCount = councils.filter(c => c.rating === 'red').length;
  const amberCount = councils.filter(c => c.rating === 'amber').length;
  const greenCount = councils.filter(c => c.rating === 'green').length;

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Councils', url: '/councils' },
  ];

  const itemListItems = councils.map((c) => ({
    name: `${c.name} Council — DfT ${c.rating.toUpperCase()} Rating`,
    url: `/councils/${c.slug}`,
    description: c.headline,
  }));

  return (
    <div className="relative">
      <SEO
        title="UK Council Road Maintenance Reports | DfT Ratings 2025-2026 | Fixtyer"
        description={`Explore DfT road maintenance ratings for ${councils.length} UK councils. ${redCount} RED rated, ${amberCount} AMBER rated, ${greenCount} GREEN rated. Ratings provide context but do not decide individual claims.`}
        canonical="/councils"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <ItemListSchema
        name="UK Council Road Maintenance Reports"
        description={`DfT road maintenance ratings for ${councils.length} UK councils.`}
        url="/councils"
        items={itemListItems}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="councils-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#councils-grid)" />
          </svg>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-brand-600 mb-8">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Councils</span>
          </div>

          {/* Stats badges */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {councils.length} council reports
            </span>
            <span className="px-3 py-1.5 bg-red-100 rounded-full text-red-700 text-sm font-semibold">
              {redCount} RED
            </span>
            <span className="px-3 py-1.5 bg-amber-100 rounded-full text-amber-700 text-sm font-semibold">
              {amberCount} AMBER
            </span>
            <span className="px-3 py-1.5 bg-emerald-100 rounded-full text-emerald-700 text-sm font-semibold">
              {greenCount} GREEN
            </span>
          </div>

          {/* Network condition snapshot */}
          <div className="mb-8 max-w-xl">
            <RatingBar red={redCount} amber={amberCount} green={greenCount} className="h-3" />
            <p className="mt-2 text-xs text-surface-600">
              Department for Transport road maintenance ratings across {councils.length} UK councils
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
            Council Road{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              Maintenance Reports
            </span>
          </h1>
          
          <p className="text-lg text-surface-800 max-w-3xl mb-4" data-speakable="summary">
            Every council below has been rated by the Department for Transport on how well they maintain their roads. 
            Find your council to review the published network-level rating and source data.
          </p>

          <p className="text-surface-700 max-w-2xl">
            Based on DfT Local Road Maintenance Ratings 2025-2026. Each report analyses condition data, spending patterns, 
            and best-practice scores. Ratings provide context, not proof of liability, and do not automatically defeat a Section 58 defence.
          </p>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Search and Filter */}
      <section className="bg-white border-b border-surface-200 sticky top-16 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search councils..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-brand-950 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
              />
            </div>
            
            {/* Rating filter */}
            <div className="flex gap-2">
              {(['all', 'red', 'amber', 'green'] as RatingFilter[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRatingFilter(r)}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    ratingFilter === r
                      ? r === 'all' 
                        ? 'bg-brand-600 text-white' 
                        : r === 'red'
                          ? 'bg-red-100 text-red-700 border border-red-300'
                          : r === 'amber'
                            ? 'bg-amber-100 text-amber-700 border border-amber-300'
                            : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      : 'bg-surface-50 text-surface-600 border border-surface-200 hover:border-surface-300 hover:bg-surface-100'
                  }`}
                >
                  {r === 'all' ? 'All' : r.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Region filter */}
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-brand-950 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
            >
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Council Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-surface-600 mb-2">No councils match your search.</p>
              <p className="text-surface-500">Try a different search term or clear your filters.</p>
              <button
                onClick={() => { setSearch(''); setRatingFilter('all'); setRegionFilter('all'); }}
                className="mt-4 px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-surface-600">
                  Showing {filtered.length} of {councils.length} councils
                </p>
                <div className="flex items-center gap-4 text-xs font-medium text-surface-600">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500" />RED
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />AMBER
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />GREEN
                  </span>
                </div>
              </div>

              {groupedByRegion ? (
                <div className="space-y-12">
                  {groupedByRegion.map((group) => (
                    <div key={group.region}>
                      <div className="mb-5 flex items-end justify-between gap-4 border-b border-surface-200 pb-3">
                        <div>
                          <h2 className="font-display text-xl font-bold text-brand-950">{group.region}</h2>
                          <p className="text-sm text-surface-600">
                            {group.list.length} council{group.list.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="hidden w-40 sm:block">
                          <RatingBar red={group.red} amber={group.amber} green={group.green} className="h-2" />
                        </div>
                      </div>
                      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {group.list.map((council) => (
                          <CouncilCard key={council.slug} council={council} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((council) => (
                    <CouncilCard key={council.slug} council={council} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Context Section */}
      <section className="bg-surface-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-brand-950 mb-8">
            What do these ratings mean for your claim?
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4 bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-red-500" />
              </div>
              <div>
                <h3 className="text-brand-950 font-semibold mb-1">RED Rating — Network-level context</h3>
                <p className="text-surface-600">
                  A RED rating is the lower DfT assessment of network-level performance. It does not prove liability,
                  establish notice of a specific pothole, or automatically defeat a Section 58 defence.
                </p>
              </div>
            </div>
            <div className="flex gap-4 bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
              </div>
              <div>
                <h3 className="text-brand-950 font-semibold mb-1">AMBER Rating — Network-level context</h3>
                <p className="text-surface-600">
                  An AMBER rating is the middle DfT assessment of network-level performance. It may identify areas
                  to investigate, but the inspection and repair evidence for the specific defect remains central.
                </p>
              </div>
            </div>
            <div className="flex gap-4 bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
              </div>
              <div>
                <h3 className="text-brand-950 font-semibold mb-1">GREEN Rating — Claims still possible</h3>
                <p className="text-surface-600">
                  A GREEN rating is a higher network-level assessment, not proof that every road was adequately
                  inspected or maintained. Individual claims still turn on their own facts and evidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">
            Don't see your council?
          </h2>
          <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
            Use our authority search tool to look up any UK highway authority's DfT rating, 
            or check the full national league table.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/authority-ratings"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
            >
              Search Your Council
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-brand-800 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
            >
              National League Table
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom stripe */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}
